import { Copy, ExternalLink } from 'lucide-react';
import { useMemo, useState } from 'react';
import { CHANNEL_LABELS, CHANNELS, type Channel } from '@marketingbee/contracts';
import { Alert, Button, Card, CardContent, EmptyState, Field, FormError, Input, PageHeader, Select, Table, TableBody, TableCell, TableHead, TableHeader, TableRow, cn, formatDate, segmentedItemClass, segmentedListClass, useToast } from '@marketingbee/ui';
import { FeatureGate, QueryView, Section, Stat } from '../components/common';
import { addDays, BrandPicker, ChannelBadge, copyText, pillarName, useActiveBrand, type BrandRow } from '../components/marketing';
import { errorMessage, trpc, type RouterOutputs } from '../lib/trpc';
import { apiBase } from '../lib/workspace';

const RANGES = [7, 30, 90] as const;
type Range = (typeof RANGES)[number];

export function ResultsPage() {
  const { brands, brand, setBrandId } = useActiveBrand();
  const [days, setDays] = useState<Range>(30);
  return (
    <div className="space-y-8">
      <PageHeader
        title="Results"
        description="Published posts, clicks on tracked links and sign-ups your site reports back. Bot and link-preview clicks are not counted."
        actions={
          <>
            <BrandPicker brands={brands} value={brand?.id} onChange={setBrandId} />
            <div className={segmentedListClass} role="group" aria-label="Period">
              {RANGES.map((r) => (
                <button key={r} type="button" className={segmentedItemClass(days === r)} aria-pressed={days === r} onClick={() => setDays(r)}>
                  {r} days
                </button>
              ))}
            </div>
          </>
        }
      />
      <FeatureGate feature="analytics">
        <Summary days={days} brand={brand} />
      </FeatureGate>
      <FeatureGate feature="link_tracking">{brand ? <Links brand={brand} /> : null}</FeatureGate>
    </div>
  );
}

function Summary({ days, brand }: { days: Range; brand: BrandRow | null }) {
  const { from, to } = useMemo(() => {
    const end = new Date();
    end.setHours(24, 0, 0, 0);
    return { from: new Date(end.getTime() - days * 86_400_000), to: end };
  }, [days]);
  const summary = trpc.analytics.summary.useQuery({ brandId: brand?.id, from, to });
  return (
    <QueryView query={summary} label="Loading results">
      {(s) => (
        <div className="space-y-8">
          <div className="grid gap-4 sm:grid-cols-3">
            <Stat label="Posts published" value={s.totals.posts.toLocaleString('en-CA')} hint={`Last ${days} days`} />
            <Stat label="Clicks" value={s.totals.clicks.toLocaleString('en-CA')} hint="On tracked links" />
            <Stat label="Sign-ups" value={s.totals.signups.toLocaleString('en-CA')} hint={s.totals.other ? `${s.totals.other} other conversions` : 'Reported by your site'} />
          </div>
          <Section title="Clicks per day">
            <Card>
              <CardContent className="py-5">
                <ClicksChart data={s.clicksByDay} days={days} />
              </CardContent>
            </Card>
          </Section>
          <div className="grid gap-6 lg:grid-cols-2">
            <Section title="By channel">
              <BreakdownTable rows={s.byChannel.map((r) => ({ key: r.channel, label: CHANNEL_LABELS[r.channel as Channel] ?? 'Other', ...r }))} />
            </Section>
            <Section title="By pillar">
              <BreakdownTable rows={s.byPillar.map((r) => ({ key: r.pillarId, label: r.pillarId === 'other' ? 'Other' : (pillarName(brand?.profile, r.pillarId) ?? r.pillarId), ...r }))} />
            </Section>
          </div>
          <Section title="Top posts">
            {s.topPosts.length === 0 ? (
              <Card>
                <EmptyState title="Nothing published in this period" description="Approved posts show up here once they are published." />
              </Card>
            ) : (
              <Card>
                <CardContent className="divide-y divide-border py-2">
                  {s.topPosts.map((p) => (
                    <div key={p.id} className="flex flex-wrap items-start justify-between gap-3 py-3 text-[14px]">
                      <div className="min-w-0 flex-1 space-y-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <ChannelBadge channel={p.channel} />
                          <span className="text-[13px] text-muted-foreground">{p.publishedAt ? formatDate(p.publishedAt) : ''}</span>
                          {pillarName(brand?.profile, p.pillarId) ? <span className="text-[13px] text-muted-foreground">· {pillarName(brand?.profile, p.pillarId)}</span> : null}
                        </div>
                        <p className="line-clamp-2">{p.title ?? p.body}</p>
                      </div>
                      <div className="flex shrink-0 items-center gap-3">
                        <span className="font-mono">{p.clicks} clicks</span>
                        {p.externalUrl ? (
                          <a href={p.externalUrl} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-foreground" aria-label="Open the post">
                            <ExternalLink className="size-4" />
                          </a>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </Section>
        </div>
      )}
    </QueryView>
  );
}

type Breakdown = { key: string; label: string; posts: number; clicks: number; signups: number };

function BreakdownTable({ rows }: { rows: Breakdown[] }) {
  return (
    <Card>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead className="text-right">Posts</TableHead>
            <TableHead className="text-right">Clicks</TableHead>
            <TableHead className="text-right">Sign-ups</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} className="text-muted-foreground">No results yet.</TableCell>
            </TableRow>
          ) : null}
          {rows.map((r) => (
            <TableRow key={r.key}>
              <TableCell className="font-medium">{r.label}</TableCell>
              <TableCell className="text-right font-mono">{r.posts}</TableCell>
              <TableCell className="text-right font-mono">{r.clicks}</TableCell>
              <TableCell className="text-right font-mono">{r.signups}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

/** Simple bar chart: one bar per day (UTC days, as the API groups them). */
function ClicksChart({ data, days }: { data: { day: string; n: number }[]; days: number }) {
  const today = new Date().toISOString().slice(0, 10);
  const byDay = new Map(data.map((d) => [d.day, d.n]));
  const series = Array.from({ length: days }, (_, i) => {
    const day = addDays(today, i - (days - 1));
    return { day, n: byDay.get(day) ?? 0 };
  });
  const max = Math.max(1, ...series.map((s) => s.n));
  const total = series.reduce((a, s) => a + s.n, 0);
  const w = 100 / series.length;
  return (
    <figure className="space-y-2">
      <svg viewBox="0 0 100 40" preserveAspectRatio="none" className="h-40 w-full" role="img" aria-label={`Clicks per day: ${total} in total, at most ${max === 1 && total === 0 ? 0 : max} in one day`}>
        {series.map((s, i) => {
          const h = (s.n / max) * 38;
          return (
            <rect key={s.day} x={i * w + w * 0.15} y={40 - h} width={w * 0.7} height={Math.max(h, s.n ? 0.6 : 0.25)} rx={0.4} className={cn(s.n ? 'fill-primary' : 'fill-muted-foreground/25')}>
              <title>{`${s.day}: ${s.n} click${s.n === 1 ? '' : 's'}`}</title>
            </rect>
          );
        })}
      </svg>
      <figcaption className="flex justify-between text-[12px] text-muted-foreground">
        <span>{series[0]?.day}</span>
        <span>Peak {max === 1 && total === 0 ? 0 : max} a day</span>
        <span>{series[series.length - 1]?.day}</span>
      </figcaption>
    </figure>
  );
}

type LinkRow = RouterOutputs['links']['list'][number];

/** The API returns each link's public URL; fall back to this origin for older responses. */
function shortUrl(l: { code: string; url?: string }): string {
  if (l.url) return l.url;
  const base = apiBase || (typeof window !== 'undefined' ? window.location.origin : '');
  return `${base}/l/${l.code}`;
}

function Links({ brand }: { brand: BrandRow }) {
  const list = trpc.links.list.useQuery({ brandId: brand.id });
  const create = trpc.links.create.useMutation();
  const utils = trpc.useUtils();
  const { success, error } = useToast();
  const [destination, setDestination] = useState(brand.profile.ctaUrl ?? '');
  const [channel, setChannel] = useState<Channel>('email');
  const [campaign, setCampaign] = useState('');
  const [content, setContent] = useState('');
  const [created, setCreated] = useState<string | null>(null);
  const copy = async (text: string) => ((await copyText(text)) ? success('Copied') : error('Could not copy', text));
  return (
    <Section title="Tracked links" description="Short links for your bio, an email signature or a printed QR code. Clicks count toward Results.">
      <Card>
        <CardContent className="space-y-3 py-5">
          {create.error ? <FormError message={errorMessage(create.error)} /> : null}
          <div className="grid gap-3 md:grid-cols-[2fr_1fr_1fr_1fr_auto] md:items-end">
            <Field label="Destination"><Input type="url" value={destination} onChange={(e) => setDestination(e.target.value)} placeholder="https://" /></Field>
            <Field label="Where you will use it"><Select value={channel} onChange={(e) => setChannel(e.target.value as Channel)} options={CHANNELS.map((c) => ({ value: c, label: CHANNEL_LABELS[c] }))} /></Field>
            <Field label="Campaign"><Input value={campaign} maxLength={80} onChange={(e) => setCampaign(e.target.value)} placeholder="spring-launch" /></Field>
            <Field label="Label (optional)"><Input value={content} maxLength={80} onChange={(e) => setContent(e.target.value)} placeholder="bio" /></Field>
            <Button
              disabled={!destination.trim() || !campaign.trim()}
              loading={create.isPending}
              onClick={() =>
                create.mutate(
                  { brandId: brand.id, destination, channel, campaign, content: content || undefined },
                  {
                    onSuccess: async (r) => {
                      setCreated(r.url);
                      setCampaign('');
                      setContent('');
                      await utils.links.list.invalidate();
                    },
                  },
                )
              }
            >
              Create tracked link
            </Button>
          </div>
          {created ? (
            <Alert variant="success" title="Your tracked link">
              <span className="flex flex-wrap items-center gap-2">
                <code className="font-mono text-[13px] text-foreground">{created}</code>
                <Button size="sm" variant="outline" onClick={() => void copy(created)}>
                  <Copy /> Copy
                </Button>
              </span>
            </Alert>
          ) : null}
        </CardContent>
      </Card>
      <QueryView query={list}>
        {(rows: LinkRow[]) =>
          rows.length === 0 ? (
            <p className="text-[14px] text-muted-foreground">No tracked links yet. Published posts get them automatically.</p>
          ) : (
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Short link</TableHead>
                    <TableHead>Destination</TableHead>
                    <TableHead>Channel</TableHead>
                    <TableHead>Campaign</TableHead>
                    <TableHead className="text-right">Clicks</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((l) => (
                    <TableRow key={l.id}>
                      <TableCell>
                        <button type="button" className="inline-flex items-center gap-1.5 font-mono text-[13px] hover:underline" onClick={() => void copy(shortUrl(l))} aria-label={`Copy ${shortUrl(l)}`}>
                          /l/{l.code} <Copy className="size-3.5 text-muted-foreground" />
                        </button>
                      </TableCell>
                      <TableCell className="max-w-72 truncate text-[13px]" title={l.destination}>{l.destination}</TableCell>
                      <TableCell>{l.channel ? <ChannelBadge channel={l.channel} /> : '—'}</TableCell>
                      <TableCell className="text-[13px]">{l.campaign ?? '—'}</TableCell>
                      <TableCell className="text-right font-mono">{l.clicks}</TableCell>
                      <TableCell className="text-[13px]">{formatDate(l.createdAt)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )
        }
      </QueryView>
    </Section>
  );
}
